import { useState, MouseEvent, FormEvent } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Container from '@mui/material/Container';
import Toolbar from '@mui/material/Toolbar';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Avatar from '@mui/material/Avatar';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import MenuIcon from '@mui/icons-material/Menu';
import LoginIcon from '@mui/icons-material/Login';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../auth/AuthContext';
import { useThemeMode } from '../theme/ThemeModeContext';
import { NotificationBell } from './NotificationBell';

const PUBLIC_NAV = [
  { key: 'explore', to: '/explore' },
  { key: 'events', to: '/events' },
  { key: 'bazaar', to: '/bazaar' },
  { key: 'forum', to: '/forum' },
] as const;

// Garage leads: it's where goals live, the screen people come back for most.
const AUTH_NAV = [
  { key: 'garage', to: '/garage' },
  { key: 'dashboard', to: '/dashboard' },
] as const;

export function Header() {
  const { user, login, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { resolvedMode } = useThemeMode();
  const logoSrc = resolvedMode === 'dark' ? '/logo_light.png' : '/logo_dark.png';
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [userMenu, setUserMenu] = useState<null | HTMLElement>(null);
  const [loginOpen, setLoginOpen] = useState(false);

  const nav = user ? [...AUTH_NAV, ...PUBLIC_NAV] : PUBLIC_NAV;

  const handleLogout = async () => {
    setUserMenu(null);
    setDrawerOpen(false);
    await logout();
    navigate('/');
  };

  const drawer = (
    <Box sx={{ width: 260 }} role="presentation" onClick={() => setDrawerOpen(false)}>
      <List>
        {nav.map((n) => (
          <ListItem key={n.to} disablePadding>
            <ListItemButton component={RouterLink} to={n.to}>
              <ListItemText primary={t(`nav.${n.key}`)} />
            </ListItemButton>
          </ListItem>
        ))}
        {user ? (
          <>
            <Divider />
            <ListItem disablePadding>
              <ListItemButton component={RouterLink} to="/contributions">
                <ListItemText primary={t('nav.contributions')} />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton component={RouterLink} to="/inbox">
                <ListItemText primary={t('nav.inbox')} />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton component={RouterLink} to="/profile">
                <ListItemText primary={t('nav.profile')} />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton onClick={handleLogout}>
                <ListItemText primary={t('nav.logout')} />
              </ListItemButton>
            </ListItem>
          </>
        ) : (
          <>
            <Divider />
            <ListItem disablePadding>
              <ListItemButton onClick={() => setLoginOpen(true)}>
                <ListItemText primary={t('nav.login')} />
              </ListItemButton>
            </ListItem>
          </>
        )}
      </List>
    </Box>
  );

  return (
    <AppBar position="static" color="default">
      <Container maxWidth="lg">
        <Toolbar disableGutters>
          <IconButton
            color="inherit"
            aria-label="open menu"
            edge="start"
            onClick={() => setDrawerOpen(true)}
            sx={{ display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>

          <Box
            component={RouterLink}
            to="/"
            sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', textDecoration: 'none' }}
          >
            <Box
              component="img"
              src={logoSrc}
              alt="Stututu"
              sx={{ height: { xs: 28, sm: 36 }, width: 'auto' }}
            />
          </Box>

          <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
            {nav.map((n) => (
              <Button key={n.to} component={RouterLink} to={n.to} color="inherit">
                {t(`nav.${n.key}`)}
              </Button>
            ))}
          </Box>

          {user ? (
            <>
              <NotificationBell />
              <IconButton
                onClick={(e: MouseEvent<HTMLElement>) => setUserMenu(e.currentTarget)}
                color="inherit"
              >
                <Avatar src={user.avatar_url ?? undefined} sx={{ width: 32, height: 32 }}>
                  {user.name?.[0]}
                </Avatar>
              </IconButton>
              <Menu anchorEl={userMenu} open={Boolean(userMenu)} onClose={() => setUserMenu(null)}>
                <MenuItem
                  component={RouterLink}
                  to="/contributions"
                  onClick={() => setUserMenu(null)}
                >
                  {t('nav.contributions')}
                </MenuItem>
                <MenuItem component={RouterLink} to="/inbox" onClick={() => setUserMenu(null)}>
                  {t('nav.inbox')}
                </MenuItem>
                <MenuItem component={RouterLink} to="/profile" onClick={() => setUserMenu(null)}>
                  {t('nav.profile')}
                </MenuItem>
                <MenuItem onClick={handleLogout}>{t('nav.logout')}</MenuItem>
              </Menu>
            </>
          ) : (
            <IconButton
              color="inherit"
              onClick={() => setLoginOpen(true)}
              aria-label={t('nav.login')}
            >
              <LoginIcon />
            </IconButton>
          )}
        </Toolbar>
      </Container>

      <Drawer anchor="left" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        {drawer}
      </Drawer>

      <LoginDialog
        open={loginOpen}
        onClose={() => setLoginOpen(false)}
        onLogin={async (email, password) => {
          await login(email, password);
          setLoginOpen(false);
          navigate('/dashboard');
        }}
      />
    </AppBar>
  );
}

function LoginDialog({
  open,
  onClose,
  onLogin,
}: {
  open: boolean;
  onClose: () => void;
  onLogin: (email: string, password: string) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await onLogin(email, password);
    } catch (err: any) {
      setError(err.message ?? t('auth.loginFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{t('auth.loginTitle')}</DialogTitle>
      <DialogContent>
        <Stack component="form" onSubmit={handleSubmit} spacing={2} sx={{ pt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label={t('auth.email')}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            fullWidth
            autoFocus
          />
          <TextField
            label={t('auth.password')}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            fullWidth
          />
          <Button type="submit" variant="contained" disabled={submitting} size="large">
            {submitting ? t('auth.loggingIn') : t('auth.loginTitle')}
          </Button>
          <Typography variant="body2" align="center">
            {t('auth.noAccount')}{' '}
            <Link component={RouterLink} to="/register" onClick={onClose}>
              {t('nav.register')}
            </Link>
          </Typography>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
